import React, { useEffect, useRef, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { api as axios } from '../../../api';
import url from '../../../serverUrl';
import './proTrading.css';

// ---------------------------------------------------------------------------
// Market stats strip next to the pair selector (Price, 24h Change, 24h
// Volume, 24h High/Low, Market Cap, Contract) - the pro-exchange header.
//
// Numbers come from /hootdex/market-summary (24h of settled Hootdex fills for
// this token), with the token row as fallback while that loads or if the
// token hasn't traded: price = token.currentPrice, change vs price24hour.
// Market cap = circulating amount x price, so it's only shown when the token
// row carries a supply.
// ---------------------------------------------------------------------------

const POLL_MS = 5000;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const priceDecimals = (p) => {
  const a = Math.abs(Number(p));
  if (!Number.isFinite(a) || a === 0) return 2;
  if (a >= 1000) return 2;
  if (a >= 1) return 4;
  return Math.min(8, Math.max(4, 2 - Math.floor(Math.log10(a)) + 2));
};

export const fmtPrice = (p) => {
  const n = num(p);
  if (n === null) return '—';
  const d = priceDecimals(n);
  return n.toLocaleString('en-US', {
    minimumFractionDigits: d,
    maximumFractionDigits: d
  });
};

const fmtUsd = (v) => {
  const n = num(v);
  if (n === null) return '—';
  return (
    n.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 }) +
    ' USD'
  );
};

const shortKey = (k) =>
  !k ? null : k.length > 14 ? `${k.slice(0, 6)}…${k.slice(-4)}` : k;

// Best available on-chain identifier for the token. Token tables don't share
// one column name, so try them in order of specificity.
export const contractOf = (t) =>
  t?.contract_key ||
  t?.contractKey ||
  t?.contract_address ||
  t?.minter ||
  t?.public_key ||
  t?.treasury_key ||
  null;

function Stat({ label, children, tip }) {
  const body = (
    <div className="pt-stat">
      <span className={`pt-stat-label${tip ? ' pt-dotted' : ''}`}>{label}</span>
      <span className="pt-stat-value">{children}</span>
    </div>
  );
  return tip ? (
    <Tooltip title={tip} arrow placement="bottom">
      {body}
    </Tooltip>
  ) : (
    body
  );
}

export default function MarketStatsBar({ token, walletConnected, walletTotal }) {
  const [summary, setSummary] = useState(null);
  const [flash, setFlash] = useState(null);
  const [copied, setCopied] = useState(false);
  const prevPrice = useRef(null);

  const displaySymbol = token?.identitySymbol || token?.token_symbol || token?.symbol;

  useEffect(() => {
    if (!displaySymbol) return undefined;
    let cancelled = false;
    let timer;
    setSummary(null);
    prevPrice.current = null;
    const load = () =>
      axios
        .get(`${url}/hootdex/market-summary`, {
          params: { symbol: displaySymbol },
          timeout: 10000
        })
        .then((res) => {
          if (!cancelled && res.data && res.data.symbol === displaySymbol) {
            setSummary(res.data);
          }
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) timer = setTimeout(load, POLL_MS);
        });
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [displaySymbol]);

  const tokenPrice = num(token?.currentPrice) ?? num(token?.token_price);
  // Same live price the listing pages show (token row, refreshed every 5s);
  // the last fill is only a fallback, so the header, listing and picker agree.
  const price = tokenPrice ?? num(summary?.last);

  // Price flash on every tick, like the order book rows.
  useEffect(() => {
    if (price === null) return undefined;
    const prev = prevPrice.current;
    prevPrice.current = price;
    if (prev === null || prev === price) return undefined;
    setFlash(price > prev ? 'up' : 'down');
    const t = setTimeout(() => setFlash(null), 700);
    return () => clearTimeout(t);
  }, [price]);

  // 24h change: the listing pages' figure (live price vs price24hour);
  // fills-based change only when the token row has no 24h reference.
  let change = null;
  let changePct = null;
  if (tokenPrice !== null && num(token?.price24hour) > 0) {
    change = tokenPrice - num(token.price24hour);
    changePct = (change / num(token.price24hour)) * 100;
  } else if (summary && num(summary.changePct) !== null) {
    change = num(summary.change);
    changePct = num(summary.changePct);
  }
  const up = (change ?? 0) >= 0;

  const supply = num(token?.amount) ?? num(token?.amount_issued);
  const marketCap = supply !== null && price !== null ? supply * price : null;
  const volumeUsd = num(summary?.volumeUsd) ?? num(token?.volume);
  const contract = contractOf(token);

  const copy = () => {
    if (!contract) return;
    navigator.clipboard?.writeText(contract).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {}
    );
  };

  return (
    <div className="pt-stats-bar">
      <Stat label="Price">
        <span className={`pt-price ${flash ? `pt-flash-${flash}` : ''}`}>
          {fmtPrice(price)}
        </span>
      </Stat>

      <Stat label="24h Change">
        <span className={change === null ? '' : up ? 'pt-up' : 'pt-down'}>
          {change === null
            ? '—'
            : `${up ? '+' : '−'}${Math.abs(change).toLocaleString('en-US', { minimumFractionDigits: priceDecimals(price), maximumFractionDigits: priceDecimals(price) })} / ${up ? '+' : '−'}${Math.abs(
                changePct
              ).toFixed(2)}%`}
        </span>
      </Stat>

      <Stat
        label="24h Volume"
        tip={
          summary?.trades
            ? `${summary.trades.toLocaleString('en-US')} trades in the last 24h`
            : undefined
        }
      >
        {fmtUsd(volumeUsd)}
      </Stat>

      {num(summary?.trades) !== null && (
        <Stat label="24h Trades" tip="Settled Hootdex fills in this market, last 24 hours">
          {Number(summary.trades).toLocaleString('en-US')}
        </Stat>
      )}

      <Stat label="24h High">
        {fmtPrice(num(summary?.high) !== null && price !== null ? Math.max(num(summary.high), price) : summary?.high)}
      </Stat>
      <Stat label="24h Low">
        {fmtPrice(num(summary?.low) !== null && price !== null ? Math.min(num(summary.low), price) : summary?.low)}
      </Stat>

      <Stat label="Market Cap" tip="Circulating supply × last price">
        {fmtUsd(marketCap)}
      </Stat>

      <Stat label="Contract">
        {contract ? (
          <Tooltip title={copied ? 'Copied' : contract} arrow>
            <button type="button" className="pt-contract" onClick={copy}>
              {shortKey(contract)}
              {copied ? (
                <CheckIcon sx={{ fontSize: 14 }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 13 }} />
              )}
            </button>
          </Tooltip>
        ) : (
          '—'
        )}
      </Stat>

      <Box sx={{ flex: 1 }} />

      <Stat label="Wallet">
        <span className={walletConnected ? '' : 'pt-down'}>
          {walletConnected
            ? `${num(walletTotal) !== null ? fmtUsd(walletTotal) : 'Connected'}`
            : 'Not connected'}
        </span>
      </Stat>
    </div>
  );
}
